import { FC, MouseEvent, useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Box,
  ClickAwayListener,
  IconButton,
  InputBase,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import EmojiPicker, { EmojiClickData } from "emoji-picker-react";
import EmojiBlackSvg from "@/assets/svg/EmojiBlackSvg";
import BackSvg from "@/assets/svg/BackSvg";
import MoreSvg from "@/assets/svg/MoreSvg";
import DotsMenu from "@/assets/svg/DotsMenu";
import PremiumCheckmark from "@/assets/svg/PremiumCheckmark";
import ArrowDiagonalSvg from "@/assets/svg/ArrowDiagonalSvg";
import ReportCommentSvg from "@/assets/svg/ReportCommentSvg";
import { LikeSvg, UnLikeSvg } from "@/assets/svg/LikeSvg";
import {
  ChatIcon,
  HeartOutlineIcon,
  ZapIcon,
  ShareIcon,
  BookmarkIcon,
  PhotoIcon,
} from "./ThreadIcons";
import theme from "@/theme";
import { styles as homeStyles } from "@/pages/Home/stylesHome";
import { formatTimeAgo } from "@/helpers/getHoursDifference";
import { formatLikesCount } from "@/helpers/formatCount";
import CustomLoader from "@/components/UI/CustomLoader/CustomLoader";
import CustomToast from "@/components/UI/CustomToast/CustomToast";
import { useGetConversationThreadQuery } from "@/store/api/conversationsApi";
import {
  useLikePostCommentMutation,
  useUnlikePostCommentMutation,
  useCreatePostCommentMutation,
} from "@/store/api/postCommentApi";
import { IThreadComment, IConversationThread } from "@/store/api/conversationsApiData";
import PostOptionsMenu from "@/components/UI/PostOptionsMenu/PostOptionsMenu";
import EngagementBadges from "./EngagementBadges";
import { useAppSelector } from "@/hooks/useAppSelector";

const CONNECTOR_COLOR = "#E4E7E9";

type ReplyStatus = "pending" | "published" | "error";

interface PendingReply {
  uid: string;
  parentUid: string;
  content: string;
  createdAt: string;
  status: ReplyStatus;
}

type ThreadNode = IThreadComment & { status?: ReplyStatus; replies?: ThreadNode[] };

const collectUids = (comment: IThreadComment | undefined, acc = new Set<string>()) => {
  if (!comment) return acc;
  acc.add(comment.uid);
  (comment.replies ?? []).forEach((reply) => collectUids(reply, acc));
  return acc;
};

const renderThreadContent = (content: string) =>
  content.split(/(@\w+)/g).map((part, i) =>
    part.startsWith("@") ? (
      <Box key={i} component="span" sx={{ color: theme.palette.custom.mainBlue }}>
        {part}
      </Box>
    ) : (
      part
    )
  );

interface ThreadPanelProps {
  conversationUid: string;
  onClose: () => void;
  onGoToPost: (postUid: string) => void;
  previewData?: IConversationThread;
}

const PostOption: FC<{ icon: React.ReactNode; count?: number }> = ({ icon, count }) => (
  <Box sx={{ display: "flex", alignItems: "center" }}>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "32px",
        height: "32px",
        borderRadius: "34px",
      }}
    >
      {icon}
    </Box>
    {count !== undefined && (
      <Box sx={{ display: "flex", justifyContent: "center", minWidth: "32px", px: "4px" }}>
        <Typography sx={{ fontFamily: "Inter", fontSize: "13px", color: "#6e767d" }}>
          {formatLikesCount(count)}
        </Typography>
      </Box>
    )}
  </Box>
);

const Comment: FC<{
  comment: ThreadNode;
  level?: number;
  defaultOpen?: boolean;
  onReply?: (comment: IThreadComment) => void;
  onRetry?: (comment: ThreadNode) => void;
}> = ({ comment, level = 0, defaultOpen = false, onReply, onRetry }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [liked, setLiked] = useState(!!comment.isLiked);
  const [likesCount, setLikesCount] = useState(comment.reactionsCount);
  const [toast, setToast] = useState<{ message: string; success: boolean } | null>(null);
  const [isOpenReplies, setIsOpenReplies] = useState(defaultOpen);
  const open = Boolean(anchorEl);
  const replies: ThreadNode[] = comment.replies ?? [];
  const hasReplies = replies.length > 0;
  const status: ReplyStatus = comment.status ?? "published";
  const isPublished = status === "published";
  const hasUnsettledReply = replies.some((reply) => reply.status && reply.status !== "published");

  useEffect(() => {
    if (hasUnsettledReply) setIsOpenReplies(true);
  }, [hasUnsettledReply]);

  const [likePostComment] = useLikePostCommentMutation();
  const [unlikePostComment] = useUnlikePostCommentMutation();
  const [isLiking, setIsLiking] = useState(false);

  const handleClick = (event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleLike = async () => {
    if (isLiking) return;

    const prevLiked = liked;
    const prevCount = likesCount;
    setLiked(!prevLiked);
    setLikesCount((count) => (prevLiked ? count - 1 : count + 1));
    setIsLiking(true);
    try {
      if (prevLiked) {
        await unlikePostComment(comment.uid).unwrap();
      } else {
        await likePostComment({ commentUid: comment.uid, type: "LIKE" }).unwrap();
      }
    } catch {
      setLiked(prevLiked);
      setLikesCount(prevCount);
      setToast({ message: "Something went wrong. Try again.", success: false });
    } finally {
      setIsLiking(false);
    }
  };

  const showRail = hasReplies && isOpenReplies;

  return (
    <Box sx={{ py: "12px", opacity: status === "pending" ? 0.6 : 1 }}>
      <Box sx={{ display: "flex", gap: "12px", alignItems: "stretch" }}>
        {toast && (
          <CustomToast
            message={toast.message}
            success={toast.success}
            onClose={() => setToast(null)}
          />
        )}
        <Box sx={{ position: "relative", width: "46px", flexShrink: 0 }}>
          <Avatar src={comment.author.image?.url || ""} sx={{ width: 46, height: 46 }} />
          {showRail && (
            <Box
              sx={{
                position: "absolute",
                left: "22px",
                top: "50px",
                bottom: "-1px",
                width: "2px",
                backgroundColor: CONNECTOR_COLOR,
                pointerEvents: "none",
              }}
            />
          )}
        </Box>
        <Box
          sx={{ flex: "1 1 0", minWidth: 0, display: "flex", flexDirection: "column", gap: "4px" }}
        >
          <Box sx={{ display: "flex", gap: "12px", alignItems: "center", width: "100%" }}>
            <Box
              sx={{
                flex: "1 1 0",
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                justifyContent: "center",
                backgroundColor: theme.palette.custom.blueGray100,
                borderRadius: "12px",
                px: "8px",
                py: "4px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "8px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flex: "1 1 0",
                    minWidth: 0,
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: "4px", minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontFamily: "Inter",
                        fontWeight: 600,
                        fontSize: "15px",
                        lineHeight: "24px",
                        color: theme.palette.custom.blueGray900,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {comment.author.username}
                    </Typography>
                    {comment.author.isPremium && (
                      <Box sx={{ display: "flex", flexShrink: 0 }}>
                        <PremiumCheckmark size={15} />
                      </Box>
                    )}
                  </Box>
                  <EngagementBadges variant="plain" like={comment.like} zap={comment.zap} />
                </Box>
                <Typography
                  sx={{
                    fontFamily: "Inter",
                    fontWeight: 500,
                    fontSize: "12px",
                    lineHeight: "16px",
                    color:
                      status === "error"
                        ? theme.palette.custom.secondaryRed
                        : theme.palette.custom.blueGray600,
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  {status === "error" ? "Not published" : formatTimeAgo(comment.createdAt)}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "20px",
                  color: theme.palette.custom.blueGray900,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                }}
              >
                {renderThreadContent(comment.content)}
              </Typography>
            </Box>
            {isPublished && (
              <IconButton
                onClick={handleClick}
                sx={{
                  p: 0,
                  height: "22px",
                  width: "22px",
                  flexShrink: 0,
                  backgroundColor: theme.palette.custom.blueGray100,
                  borderRadius: "50%",
                  "&:hover": { backgroundColor: "#e9ebee" },
                }}
              >
                <DotsMenu />
              </IconButton>
            )}
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              PaperProps={homeStyles.postSelectDropdownMenu}
            >
              <MenuItem
                onClick={() => {
                  setToast({ message: "Comment reported", success: true });
                  handleClose();
                }}
                sx={{
                  gap: "4px",
                  height: "32.1px",
                  borderRadius: "8px",
                  padding: "6px 13px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <ListItemIcon sx={{ minWidth: "0px !important" }}>
                  <ReportCommentSvg style={{ width: "1rem", height: "1rem" }} />
                </ListItemIcon>
                <ListItemText>
                  <Typography
                    sx={{
                      color: "#CE212B",
                      fontFamily: "Inter",
                      fontWeight: 500,
                      fontSize: "14px",
                      lineHeight: "20px",
                    }}
                  >
                    Report comment
                  </Typography>
                </ListItemText>
              </MenuItem>
            </Menu>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: "20px", py: "4px", pr: "46px" }}>
            {status === "pending" && (
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "16px",
                  color: theme.palette.custom.blueGray600,
                }}
              >
                Publishing...
              </Typography>
            )}
            {status === "error" && (
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "16px",
                  whiteSpace: "pre-wrap",
                }}
              >
                <Box component="span" sx={{ color: "#2A2D31" }}>
                  Something went wrong. Please{" "}
                </Box>
                <Box
                  component="span"
                  onClick={() => onRetry?.(comment)}
                  sx={{
                    color: theme.palette.custom.mainBlue,
                    textDecoration: "underline",
                    cursor: "pointer",
                  }}
                >
                  Try again
                </Box>
              </Typography>
            )}
            {isPublished && (
              <>
                <Box
                  onClick={handleLike}
                  sx={{ display: "flex", alignItems: "center", gap: "2px", cursor: "pointer" }}
                >
                  {liked ? <LikeSvg /> : <UnLikeSvg />}
                  <Typography
                    sx={{
                      fontFamily: "Inter",
                      fontWeight: 500,
                      fontSize: "14px",
                      lineHeight: "16px",
                      color: liked
                        ? theme.palette.custom.mainBlue
                        : theme.palette.custom.blueGray900,
                    }}
                  >
                    {formatLikesCount(likesCount)}
                  </Typography>
                </Box>
                <Typography
                  onClick={() => onReply?.(comment)}
                  sx={{
                    fontFamily: "Inter",
                    fontWeight: 500,
                    fontSize: "14px",
                    lineHeight: "16px",
                    color: theme.palette.custom.blueGray900,
                    cursor: "pointer",
                  }}
                >
                  Reply
                </Typography>
                {hasReplies && (
                  <Typography
                    onClick={() => setIsOpenReplies((prev) => !prev)}
                    sx={{
                      fontFamily: "Inter",
                      fontWeight: 500,
                      fontSize: "14px",
                      lineHeight: "16px",
                      color: theme.palette.custom.mainBlue,
                      cursor: "pointer",
                    }}
                  >
                    {isOpenReplies
                      ? "Hide replies"
                      : `View all ${replies.length} ${replies.length === 1 ? "reply" : "replies"}`}
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>

      {showRail && (
        <Box sx={{ ml: "58px" }}>
          {replies.map((child, index) => {
            const isLast = index === replies.length - 1;
            return (
              <Box key={child.uid} sx={{ position: "relative" }}>
                <Box
                  sx={{
                    position: "absolute",
                    left: "-36px",
                    top: "-1px",
                    ...(isLast ? { height: "20px" } : { bottom: 0 }),
                    width: "2px",
                    backgroundColor: CONNECTOR_COLOR,
                    pointerEvents: "none",
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    left: "-36px",
                    top: "19px",
                    width: "38px",
                    height: "16px",
                    borderLeft: `2px solid ${CONNECTOR_COLOR}`,
                    borderBottom: `2px solid ${CONNECTOR_COLOR}`,
                    borderBottomLeftRadius: "16px",
                    pointerEvents: "none",
                  }}
                />
                <Comment comment={child} level={level + 1} onReply={onReply} onRetry={onRetry} />
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

const ThreadPanel: FC<ThreadPanelProps> = ({
  conversationUid,
  onClose,
  onGoToPost,
  previewData,
}) => {
  const { data: fetched, isFetching } = useGetConversationThreadQuery(conversationUid, {
    refetchOnMountOrArgChange: true,
    skip: !!previewData,
  });
  const data = previewData ?? fetched;
  const [createPostComment, { isLoading: isSending }] = useCreatePostCommentMutation();
  const [reply, setReply] = useState("");
  const [replyTarget, setReplyTarget] = useState<{ uid: string; username: string } | null>(null);
  const [postMenuAnchor, setPostMenuAnchor] = useState<HTMLElement | null>(null);
  const [pendingReplies, setPendingReplies] = useState<PendingReply[]>([]);
  const { user, subscriptionStatus } = useAppSelector((state) => state.auth);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const replyInputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    const el = replyInputRef.current;
    const emoji = emojiData.emoji;

    if (el) {
      const start = el.selectionStart ?? reply.length;
      const end = el.selectionEnd ?? reply.length;
      const nextValue = reply.slice(0, start) + emoji + reply.slice(end);
      setReply(nextValue);

      requestAnimationFrame(() => {
        el.focus();
        const pos = start + emoji.length;
        el.setSelectionRange(pos, pos);
      });
    } else {
      setReply((prev) => prev + emoji);
    }

    setShowEmojiPicker(false);
  };

  const post = data?.post;

  const serverComment = useMemo<IThreadComment | undefined>(
    () => (data?.root ? { ...data.root, replies: data.replies ?? [] } : undefined),
    [data]
  );

  const publishReply = async (item: PendingReply) => {
    if (!post) return;
    try {
      const created = await createPostComment({
        content: item.content,
        postUid: post.uid,
        replyToUid: item.parentUid,
      }).unwrap();

      setPendingReplies((prev) =>
        prev.map((pending) => {
          if (pending.uid === item.uid) {
            return { ...pending, uid: created?.uid ?? pending.uid, status: "published" };
          }
          if (pending.parentUid === item.uid && created?.uid) {
            return { ...pending, parentUid: created.uid };
          }
          return pending;
        })
      );
    } catch (error) {
      console.error("Failed to send reply:", error);
      setPendingReplies((prev) =>
        prev.map((pending) =>
          pending.uid === item.uid ? { ...pending, status: "error" } : pending
        )
      );
    }
  };

  const handleSend = () => {
    const trimmed = reply.trim();
    if (!trimmed || !post || isSending) return;

    const item: PendingReply = {
      uid: crypto.randomUUID(),
      parentUid: replyTarget?.uid ?? conversationUid,
      content: trimmed,
      createdAt: new Date().toISOString(),
      status: "pending",
    };

    setPendingReplies((prev) => [...prev, item]);
    setReply("");
    setReplyTarget(null);
    publishReply(item);
  };

  const handleRetry = (comment: ThreadNode) => {
    const item = pendingReplies.find((pending) => pending.uid === comment.uid);
    if (!item) return;
    setPendingReplies((prev) =>
      prev.map((pending) =>
        pending.uid === item.uid ? { ...pending, status: "pending" } : pending
      )
    );
    publishReply({ ...item, status: "pending" });
  };

  const rootComment = useMemo<ThreadNode | undefined>(() => {
    if (!serverComment) return undefined;

    const serverUids = collectUids(serverComment);
    const visiblePending = pendingReplies.filter((pending) => !serverUids.has(pending.uid));

    function toNode(pending: PendingReply): ThreadNode {
      return {
        uid: pending.uid,
        content: pending.content,
        contentPreview: pending.content,
        createdAt: pending.createdAt,
        author: {
          uid: user?.uid ?? "",
          username: user?.username ?? "",
          image: user?.image?.url ? { url: user.image.url } : null,
          isPremium: subscriptionStatus === "ACTIVE",
        },
        reactionsCount: 0,
        repliesCount: 0,
        isLiked: false,
        status: pending.status,
        replies: childrenOf(pending.uid),
      };
    }

    function childrenOf(...parentUids: string[]): ThreadNode[] {
      return visiblePending.filter((pending) => parentUids.includes(pending.parentUid)).map(toNode);
    }

    function merge(comment: IThreadComment, isRoot = false): ThreadNode {
      const ownPending =
        isRoot && comment.uid !== conversationUid
          ? childrenOf(comment.uid, conversationUid)
          : childrenOf(comment.uid);
      return {
        ...comment,
        replies: [...(comment.replies ?? []).map((child) => merge(child)), ...ownPending],
      };
    }

    return merge(serverComment, true);
  }, [serverComment, pendingReplies, user, subscriptionStatus, conversationUid]);

  const addedRepliesCount = pendingReplies.filter((item) => item.status !== "error").length;
  const canSend = !!reply.trim() && !isSending;

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        right: "max(0px, calc((100vw - 1440px) / 2))",
        zIndex: 30,
        width: "min(632px, 100vw)",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflowX: "hidden",
        backgroundColor: theme.palette.custom.mainWhite,
        borderLeft: "1px solid #f5f5f5",
        boxShadow: "-1px 0px 8px rgba(43,47,62,0.15)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          height: "54px",
          px: "16px",
          flexShrink: 0,
        }}
      >
        <IconButton onClick={onClose} size="small" sx={{ p: "2px" }}>
          <BackSvg />
        </IconButton>
        <Typography
          sx={{ fontFamily: "Inter", fontWeight: 600, fontSize: "18px", color: "#383c40" }}
        >
          Thread
        </Typography>
      </Box>

      <Box sx={{ flex: "1 1 0", minHeight: 0, overflowY: "auto" }} className="hide-scrollbar">
        {isFetching ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: "60px" }}>
            <CustomLoader size={40} />
          </Box>
        ) : !post ? (
          <Box
            sx={{ p: "40px 16px", textAlign: "center", color: theme.palette.custom.blueGray600 }}
          >
            <Typography sx={{ fontSize: "14px" }}>Thread unavailable</Typography>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                backgroundColor: "rgba(125,134,144,0.05)",
                borderBottom: "1px solid #f4f5f6",
                pl: "16px",
                pr: "20px",
                py: "16px",
                display: "flex",
                flexDirection: "column",
                gap: "10px",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography
                  sx={{
                    fontFamily: "SF Pro Text, Inter, sans-serif",
                    fontWeight: 700,
                    fontSize: "12px",
                    textTransform: "uppercase",
                    color: "#9aa0b4",
                  }}
                >
                  Original post
                </Typography>
                <Box
                  onClick={() => onGoToPost(post.uid)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    cursor: "pointer",
                    "& svg path": { stroke: "#9aa0b4" },
                  }}
                >
                  <ArrowDiagonalSvg size={16} direction="up-right" />
                  <Typography sx={{ fontFamily: "Inter", fontSize: "12px", color: "#9aa0b4" }}>
                    Go to post
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  backgroundColor: "#fff",
                  borderRadius: "10px",
                  p: "10px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                  <Box
                    sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                      <Typography
                        sx={{
                          fontFamily: "SF Pro Text, Inter, sans-serif",
                          fontWeight: 700,
                          fontSize: "15px",
                          color: "#3c3a3a",
                        }}
                      >
                        @{post.author.username}
                      </Typography>
                      <PremiumCheckmark size={20} />
                      <Typography
                        sx={{
                          fontFamily: "Inter",
                          fontWeight: 500,
                          fontSize: "12px",
                          lineHeight: "16px",
                          color: theme.palette.custom.blueGray600,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {formatTimeAgo(post.createdAt)}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => setPostMenuAnchor(e.currentTarget)}
                      sx={{ p: 0 }}
                    >
                      <MoreSvg variant="outlined" />
                    </IconButton>
                    <PostOptionsMenu
                      postUid={post.uid}
                      authorUid={post.author.uid}
                      anchorEl={postMenuAnchor}
                      open={Boolean(postMenuAnchor)}
                      onClose={() => setPostMenuAnchor(null)}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: "Inter",
                      fontWeight: 400,
                      fontSize: "14px",
                      lineHeight: "17px",
                      letterSpacing: "0.14px",
                      color: "#2a2d31",
                      wordBreak: "break-word",
                      overflowWrap: "anywhere",
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {post.contentPreview}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    height: "32px",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: "2px" }}>
                    <PostOption
                      icon={<ChatIcon />}
                      count={post.commentsCount + addedRepliesCount}
                    />
                    <PostOption icon={<HeartOutlineIcon />} count={post.likesCount} />
                    <PostOption icon={<ZapIcon />} count={post.zapsCount} />
                    <PostOption icon={<ShareIcon />} />
                    <PostOption icon={<BookmarkIcon />} />
                  </Box>
                  <Box sx={{ display: "flex" }}>
                    <PhotoIcon size={24} />
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box sx={{ px: "16px", pt: "10px", pb: "4px" }}>
              {rootComment && (
                <Comment
                  key={rootComment.uid}
                  comment={rootComment}
                  defaultOpen
                  onReply={(c) => setReplyTarget({ uid: c.uid, username: c.author.username })}
                  onRetry={handleRetry}
                />
              )}
            </Box>
          </>
        )}
      </Box>

      <Box
        sx={{
          backgroundColor: "#fff",
          borderTop: "1px solid #f4f5f6",
          pt: "12px",
          pb: "16px",
          px: "20px",
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
          <Avatar src={user?.image?.url || ""} sx={{ width: 46, height: 46, flexShrink: 0 }} />
          <Box
            sx={{
              flex: "1 1 0",
              minWidth: 0,
              backgroundColor: theme.palette.custom.blueGray100,
              borderRadius: "12px",
              p: "8px",
              display: "flex",
              flexDirection: "column",
              gap: "10px",
            }}
          >
            {replyTarget && (
              <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <Typography sx={{ fontFamily: "Inter", fontSize: "12px", color: "#6e767d" }}>
                  Replying to{" "}
                  <Box component="span" sx={{ color: theme.palette.custom.mainBlue }}>
                    @{replyTarget.username}
                  </Box>
                </Typography>
                <Box
                  onClick={() => setReplyTarget(null)}
                  sx={{ cursor: "pointer", fontSize: "12px", lineHeight: "12px", color: "#9da6b0" }}
                >
                  ✕
                </Box>
              </Box>
            )}
            <InputBase
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              inputRef={replyInputRef}
              placeholder={replyTarget ? `Reply to @${replyTarget.username}...` : "Your reply..."}
              multiline
              maxRows={4}
              sx={{
                width: "100%",
                fontFamily: "Inter",
                fontSize: "14px",
                lineHeight: "22px",
                color: theme.palette.custom.blueGray900,
                "& .MuiInputBase-input::placeholder": { color: "#9da6b0", opacity: 1 },
              }}
            />
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", position: "relative" }}>
                <EmojiBlackSvg onClick={() => setShowEmojiPicker((prev) => !prev)} cursor="pointer" />
                {showEmojiPicker && (
                  <ClickAwayListener onClickAway={() => setShowEmojiPicker(false)}>
                    <Box
                      sx={{ position: "absolute", bottom: "100%", left: 0, mb: "8px", zIndex: 10 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <EmojiPicker onEmojiClick={handleEmojiClick} height={363} />
                    </Box>
                  </ClickAwayListener>
                )}
              </Box>
              <Box
                onClick={handleSend}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  px: "16px",
                  py: "8px",
                  borderRadius: "8px",
                  backgroundColor: canSend ? theme.palette.custom.mainBlue : "#8fbdf9",
                  cursor: canSend ? "pointer" : "default",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Inter",
                    fontWeight: 700,
                    fontSize: "13px",
                    letterSpacing: "-0.13px",
                    color: "#fcfcfc",
                  }}
                >
                  Send
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ThreadPanel;
